import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../../components/Layout";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Boxes,
  Package,
  ArrowLeftRight,
  PencilLine,
  Plus,
  Eraser,
  FileText,
  Inbox,
  ArrowRight,
} from "lucide-react";

import ItemsTab from "./ItemsTab";
import StockTab from "./StockTab";
import TransfersTab from "./TransfersTab";
import MovementsTab from "./MovementsTab";
import ReportsTab from "./ReportsTab";
import AnalyticsSlot from "./AnalyticsSlot";
import AssetValueBlock from "./AssetValueBlock";

import AddItemDialog from "./dialogs/AddItemDialog";
import AdjustStockDialog from "./dialogs/AdjustStockDialog";
import TransferDialog from "./dialogs/TransferDialog";
import WriteOffDialog from "./dialogs/WriteOffDialog";
import TransferNoteDialog from "./dialogs/TransferNoteDialog";

const OLIVE_SOFT = "#F4F6EC";
const OLIVE_BORDER = "#D9E0BD";

const KPI_LABELS = [
  { key: "total_items", label: "Total Items", icon: Package, suffix: "" },
  { key: "total_stock_units", label: "Stock Units", icon: Boxes, suffix: "" },
  { key: "transfers_period", label: "Transfers · 30D", icon: ArrowLeftRight, suffix: "" },
  { key: "write_offs_period", label: "Write-Offs · 30D", icon: Eraser, suffix: "" },
];

/**
 * Inventory home (Phase 6 Part 2a frontend).
 *  • Real KPIs from /api/inventory/kpis
 *  • Persistent action toolbar (Add Item / Adjust / Transfer / Write-Off)
 *  • Tabs: Items · Stock · Transfers · Movements
 *  • Transfer history opens a printable transfer-note dialog
 *  • Empty states for every tab
 */
export default function InventoryHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [activeTab, setActiveTab] = useState("items");
  const [refreshTick, setRefreshTick] = useState(0); // bumped after writes

  // Dialog open state
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [transferNote, setTransferNote] = useState(null); // transfer doc

  // Lookup data for dialogs
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);

  const canView = user?.role === "admin" || !!user?.permissions_effective?.view_inventory;
  const canManage = user?.role === "admin" || !!user?.permissions_effective?.manage_inventory;
  const canTransfer = user?.role === "admin" || !!user?.permissions_effective?.transfer_inventory;

  const loadKpis = useCallback(async () => {
    try {
      setLoadingKpis(true);
      const r = await api.get("/inventory/kpis");
      setKpis(r.data);
    } catch (e) {
      setKpis({ total_items: 0, total_stock_units: 0, transfers_period: 0, write_offs_period: 0 });
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [itemsR, sitesR, catsR] = await Promise.all([
        api.get("/inventory/items", { params: { active: true } }),
        api.get("/sites"),
        api.get("/inventory/categories"),
      ]);
      setItems(itemsR.data || []);
      setSites(sitesR.data?.sites || []);
      setCategories(catsR.data || []);
    } catch (e) {
      /* gated users still get empty lists */
    }
  }, []);

  useEffect(() => {
    if (canView) {
      loadKpis();
      loadLookups();
    } else {
      setLoadingKpis(false);
    }
  }, [canView, loadKpis, loadLookups, refreshTick]);

  const handleSuccess = (msg) => {
    if (msg) toast.success(msg);
    setRefreshTick((t) => t + 1);
  };

  const heroSubtitle = useMemo(() => {
    if (loadingKpis) return "Loading inventory…";
    if (!kpis) return "Inventory home";
    const lt = kpis.last_transfer;
    if (lt) {
      return `Last transfer · ${lt.source_site} → ${lt.destination_site} · ${new Date(lt.date).toLocaleDateString()}`;
    }
    return "No transfers recorded yet. Start by adding items and tracking stock per site.";
  }, [loadingKpis, kpis]);

  if (!canView) {
    return (
      <AppShell title="Inventory">
        <div className="px-6 lg:px-10 py-10">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="inv-no-access">
            <Boxes className="mx-auto text-slate-300" size={48} strokeWidth={1.4} />
            <div className="mt-3 text-base font-semibold text-slate-900">No inventory access</div>
            <p className="text-sm text-slate-500 mt-1">
              Ask an admin to grant the <span className="font-medium">view_inventory</span> permission to view this workspace.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Inventory">
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-6">
        {/* ──────────── HERO + KPIs + PERSISTENT ACTIONS ──────────── */}
        <section
          data-testid="inv-hero"
          className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-7 shadow-[0_2px_8px_rgba(15,23,42,0.04)]"
        >
          <div className="flex flex-col lg:flex-row lg:items-start gap-5 lg:gap-6">
            <div
              className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl grid place-items-center shrink-0 text-olive"
              style={{ backgroundColor: OLIVE_SOFT }}
            >
              <Boxes size={26} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Inventory workspace
              </div>
              <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight mt-0.5">
                Stock, transfers &amp; write-offs
              </h1>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{heroSubtitle}</p>
            </div>
            <ActionToolbar
              canManage={canManage}
              canTransfer={canTransfer}
              hasItems={items.length > 0}
              hasSites={sites.length > 0}
              onAddItem={() => setAddItemOpen(true)}
              onAdjust={() => setAdjustOpen(true)}
              onTransfer={() => setTransferOpen(true)}
              onWriteOff={() => setWriteOffOpen(true)}
            />
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mt-6" data-testid="inv-kpis">
            {KPI_LABELS.map(({ key, label, icon: Icon }) => (
              <KpiTile
                key={key}
                testid={`kpi-${key}`}
                label={label}
                value={kpis?.[key]}
                loading={loadingKpis}
                Icon={Icon}
              />
            ))}
          </div>

          {/* Period analytics — Part 2b */}
        </section>

        <AssetValueBlock refreshTick={refreshTick} />

        <AnalyticsSlot
          refreshTick={refreshTick}
          onSeeReports={() => navigate("/inventory/reports")}
        />

        {/* ──────────── TABS ──────────── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            data-testid="inv-tabs"
            className="bg-white border border-slate-200 rounded-2xl p-1 grid grid-cols-5 gap-1 w-full lg:w-auto lg:inline-grid"
          >
            <TabsTrigger value="items" data-testid="tab-items" className="rounded-xl data-[state=active]:bg-olive data-[state=active]:text-white text-sm">Items</TabsTrigger>
            <TabsTrigger value="stock" data-testid="tab-stock" className="rounded-xl data-[state=active]:bg-olive data-[state=active]:text-white text-sm">Stock</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="tab-transfers" className="rounded-xl data-[state=active]:bg-olive data-[state=active]:text-white text-sm">Transfers</TabsTrigger>
            <TabsTrigger value="movements" data-testid="tab-movements" className="rounded-xl data-[state=active]:bg-olive data-[state=active]:text-white text-sm">Movements</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-xl data-[state=active]:bg-olive data-[state=active]:text-white text-sm">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="mt-5">
            <ItemsTab items={items} categories={categories} refreshTick={refreshTick} />
          </TabsContent>
          <TabsContent value="stock" className="mt-5">
            <StockTab sites={sites} items={items} refreshTick={refreshTick} />
          </TabsContent>
          <TabsContent value="transfers" className="mt-5">
            <TransfersTab
              sites={sites}
              refreshTick={refreshTick}
              onOpenNote={(t) => setTransferNote(t)}
            />
          </TabsContent>
          <TabsContent value="movements" className="mt-5">
            <MovementsTab sites={sites} items={items} refreshTick={refreshTick} />
          </TabsContent>
          <TabsContent value="reports" className="mt-5">
            <ReportsTab
              onOpenReconciliation={() => navigate("/inventory/reports")}
              onOpenWriteOffs={() => navigate("/inventory/write-offs")}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* ──────────── DIALOGS ──────────── */}
      <AddItemDialog
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        categories={categories}
        sites={sites}
        onSuccess={() => handleSuccess("Item created")}
      />
      <AdjustStockDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        items={items}
        sites={sites}
        onSuccess={() => handleSuccess("Stock adjusted")}
      />
      <TransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        items={items}
        sites={sites}
        onSuccess={(transferDoc) => {
          handleSuccess(`Transfer ${transferDoc?.transfer_id || ""} completed`);
          if (transferDoc) setTransferNote(transferDoc);
        }}
      />
      <WriteOffDialog
        open={writeOffOpen}
        onOpenChange={setWriteOffOpen}
        items={items}
        sites={sites}
        onSuccess={() => handleSuccess("Write-off recorded")}
      />
      <TransferNoteDialog
        open={!!transferNote}
        onOpenChange={(v) => !v && setTransferNote(null)}
        transfer={transferNote}
      />
    </AppShell>
  );
}

// ───────────────────────── KPI TILE ─────────────────────────
const KpiTile = ({ label, value, loading, Icon, testid }) => (
  <div
    data-testid={testid}
    className="bg-slate-50/60 border border-slate-200 rounded-2xl p-4 lg:p-5 hover:bg-white transition-colors"
  >
    <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
      {Icon && <Icon size={13} strokeWidth={1.9} />}
      {label}
    </div>
    <div className="text-3xl lg:text-4xl font-semibold tracking-tight mt-2 text-slate-900">
      {loading ? "—" : (value ?? 0)}
    </div>
  </div>
);

// ───────────────────────── ACTION TOOLBAR ─────────────────────────
const ActionToolbar = ({ canManage, canTransfer, hasItems, hasSites, onAddItem, onAdjust, onTransfer, onWriteOff }) => {
  const needsCatalogue = !hasItems;
  const needsSites = !hasSites;
  const disabledTitle = needsCatalogue
    ? "Add an item first to unlock this action."
    : needsSites
      ? "No sites configured yet."
      : "";
  return (
  <div
    data-testid="inv-action-toolbar"
    className="flex flex-wrap gap-2 lg:flex-col lg:items-end lg:gap-2 shrink-0"
  >
    {canManage && (
      <button
        data-testid="action-add-item"
        onClick={onAddItem}
        className="rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-olive text-white hover:opacity-90 transition-opacity"
      >
        <Plus size={13} strokeWidth={2.2} /> Add item
      </button>
    )}
    {canManage && (
      <button
        data-testid="action-adjust"
        onClick={onAdjust}
        disabled={needsCatalogue || needsSites}
        title={disabledTitle || undefined}
        className="rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PencilLine size={13} strokeWidth={2} /> Adjust stock
      </button>
    )}
    {canTransfer && (
      <button
        data-testid="action-transfer"
        onClick={onTransfer}
        disabled={needsCatalogue || needsSites}
        title={disabledTitle || undefined}
        className="rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeftRight size={13} strokeWidth={2} /> Transfer
      </button>
    )}
    {canManage && (
      <button
        data-testid="action-write-off"
        onClick={onWriteOff}
        disabled={needsCatalogue || needsSites}
        title={disabledTitle || undefined}
        className="rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Eraser size={13} strokeWidth={2} /> Write-off
      </button>
    )}
  </div>
  );
};

// ───────────────────────── EMPTY STATE (shared) ─────────────────────────
export const EmptyState = ({ icon: Icon = Inbox, title, body, action, testid }) => (
  <div
    data-testid={testid || "empty-state"}
    className="bg-white border border-slate-200 rounded-2xl p-10 text-center"
  >
    <div
      className="w-12 h-12 mx-auto rounded-2xl grid place-items-center text-olive"
      style={{ backgroundColor: OLIVE_SOFT }}
    >
      <Icon size={22} strokeWidth={1.6} />
    </div>
    <div className="mt-3 text-base font-semibold text-slate-900">{title}</div>
    {body && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{body}</p>}
    {action && (
      <button
        onClick={action.onClick}
        data-testid={action.testid}
        className="mt-4 rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-olive text-white hover:opacity-90 transition-opacity"
      >
        {action.label} <ArrowRight size={13} strokeWidth={2.2} />
      </button>
    )}
  </div>
);
